import React, { useRef, useState, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  Separator,
} from "@fluentui/react";
import "./esign-document.css";

interface ESignDocumentProps {
  document: {
    id: number;
    title: string;
  };
  htmlContent: string;
  onContinue: () => void;
  onSubmit: (signatureData: string) => Promise<void>;
  isSubmitting?: boolean;
}

const ESignDocument: React.FC<ESignDocumentProps> = ({
  document: doc,
  htmlContent,
  onContinue,
  onSubmit,
  isSubmitting = false,
}) => {
  const sigPadRef = useRef<SignatureCanvas>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const currentDate = new Date().toLocaleDateString();

  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (!node) return;
    const updateSize = () => {
      const cssWidth = node.clientWidth;
      if (cssWidth <= 0) return;
      const cssHeight = Math.min(Math.round(cssWidth * 0.35), 300);
      const canvas = sigPadRef.current?.getCanvas();
      if (canvas) {
        canvas.width = cssWidth;
        canvas.height = cssHeight;
        sigPadRef.current?.clear();
      }
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  const handleClear = () => {
    if (sigPadRef.current) {
      const pad = sigPadRef.current.getSignaturePad();
      pad.off();
      sigPadRef.current.clear();
      pad.on();
    }
    setSignatureError(false);
  };

  const handleSubmit = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      setSignatureError(true);
      return;
    }

    setSignatureError(false);
    setApiError(null);

    try {
      const signatureData = sigPadRef.current.toDataURL("image/png");
      await onSubmit(signatureData);
      setShowSuccessDialog(true);
    } catch (error) {
      setApiError(
        error instanceof Error
          ? error.message
          : "An error occurred while submitting.",
      );
    }
  };

  const handleContinue = () => {
    setShowSuccessDialog(false);
    onContinue();
  };

  const dialogContentProps = {
    type: DialogType.normal,
    title: "Thank you for signing the document",
    subText: `Document saved successfully - ${doc.title}. Please click Continue to go back to E-Sign Main View.`,
  };

  const modalProps = {
    isBlocking: true,
  };

  return (
    <>
      <Dialog
        hidden={!showSuccessDialog}
        onDismiss={() => setShowSuccessDialog(false)}
        dialogContentProps={dialogContentProps}
        modalProps={modalProps}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={handleContinue}
            text="Continue"
            styles={{
              root: {
                backgroundColor: "#00345D !important",
                color: "white !important",
                border: "none",
              },
              rootHovered: {
                backgroundColor: "#004a7f !important",
                color: "white !important",
              },
              icon: { color: "white !important" },
            }}
          />
        </DialogFooter>
      </Dialog>

      <div className="esign-document-card">
        <div className="esign-document-content">
          <div className="esign-document-title-container">
            <Text variant="xLarge" className="esign-document-title">
              E-Sign {doc.title}
            </Text>
          </div>

          <Separator />

          <div
            className="esign-document-html-content"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          <Separator />

          <div className="esign-signature-section">
            <div
              ref={containerCallbackRef}
              className="esign-signature-container"
            >
              <SignatureCanvas
                ref={sigPadRef}
                penColor="blue"
                clearOnResize={false}
                canvasProps={{
                  style: { display: "block" },
                  className: "esign-signature-canvas",
                }}
              />
              <div className="esign-signature-date">
                <Text variant="small" className="esign-signature-date-text">
                  <strong>Signature Date:</strong> {currentDate}
                </Text>
              </div>
            </div>

            <Stack
              horizontal
              horizontalAlign="center"
              tokens={{ childrenGap: 12 }}
              className="esign-action-buttons"
            >
              <DefaultButton
                text="Clear"
                onClick={handleClear}
                disabled={isSubmitting}
                styles={{
                  root: { color: "#00345D", borderColor: "#00345D" },
                  rootHovered: { color: "#00345D", borderColor: "#002a4a" },
                }}
              />
              <PrimaryButton
                onClick={handleSubmit}
                disabled={isSubmitting}
                styles={{
                  root: {
                    backgroundColor: "#00345D !important",
                    color: "white !important",
                    border: "none",
                  },
                  rootHovered: {
                    backgroundColor: "#004a7f !important",
                    color: "white !important",
                  },
                  icon: { color: "white !important" },
                }}
              >
                {isSubmitting ? (
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                  >
                    <Spinner size={SpinnerSize.small} />
                    <span>Submitting...</span>
                  </Stack>
                ) : (
                  "Submit"
                )}
              </PrimaryButton>
            </Stack>

            {signatureError && (
              <MessageBar
                messageBarType={MessageBarType.error}
                className="esign-error-message"
              >
                Please draw your signature before submitting.
              </MessageBar>
            )}
            {apiError && (
              <MessageBar
                messageBarType={MessageBarType.error}
                className="esign-error-message"
              >
                {apiError}
              </MessageBar>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ESignDocument;
